/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import * as yup from 'yup';
import { cloneDeep } from 'lodash';
import { personHandler } from '../../person';

function handleInitialAdjustments({
  questionnaire,
  users,
  accessGroups,
  setFieldValue
}) {
  try {
    return questionnaire.map(question => {
      let answerQuestion = question.answerOptions?.find(ansOpt => ansOpt.idAnswerOption == question.idAnswerOption)

      if (answerQuestion && [38].includes(answerQuestion.idDomain) && question.idQuestionRoot) {
        questionnaire = questionnaire.map(qst => {
          if ([question.idQuestionRoot].includes(qst.idQuestion)) {
            qst.behaviorObject.isRefusedByHiddenQuestions = true;
          }
          return qst;
        })
      }

      if (question) {
        question.idQuestionnaireAnswers = null
        question.idQuestionnaireAnswersParent = null
        question.idQuestionnaireAnswersRoot = null

        if (question?.childrenQuestions?.length) {
          question.childrenQuestions = question.childrenQuestions.map(childQuestion => {
            childQuestion.idQuestionnaireAnswers = null
            childQuestion.idQuestionnaireAnswersParent = null
            childQuestion.idQuestionnaireAnswersRoot = null
            return childQuestion
          })
        }
      }

      if (users) {
        if (question.idQuestion === 2425) {
          question.answer = users?.userName;
          //question.behaviorObject.isReadOnly = true;
          setFieldValue && setFieldValue(question.key, users?.userName)
        }
        
        if (question.idQuestion === 1964) {
          const singleSignOn = users.idDomain === 741 ? true : false;
          const selectedOption = question.answerOptions.find(v => v.idAnswerOption === (singleSignOn ? 7613 : 7612));

          if (selectedOption) {
            const result = {
              idAnswerOption: selectedOption?.idAnswerOption,
              dsAnswerOption: selectedOption?.dsAnswerOption,
              resultValue: selectedOption?.behaviorObject?.resultValue || null
            };

            question.answer = selectedOption?.dsAnswerOption;
            question.answerJson = selectedOption.answerJson;
            question.idAnswerOption = selectedOption?.idAnswerOption;
            setFieldValue && setFieldValue(question.key, result);
          }
        }

        if (question.idQuestion === 1965) {
          let MFA = users?.userMetadata?.verificationCodeEnable;
          if (typeof users?.userMetadata === 'string') {
            MFA = JSON.parse(users?.userMetadata).verificationCodeEnable;
          }
          const selectedOption = question.answerOptions.find(v => v.idAnswerOption === (MFA ? 7615 : 7614));

          if (selectedOption) {
            const result = {
              idAnswerOption: selectedOption?.idAnswerOption,
              dsAnswerOption: selectedOption?.dsAnswerOption,
              resultValue: selectedOption?.behaviorObject?.resultValue || null
            };

            question.answer = selectedOption?.dsAnswerOption;
            question.answerJson = selectedOption.answerJson;
            question.idAnswerOption = selectedOption?.idAnswerOption;
            setFieldValue && setFieldValue(question.key, result);
          }
        }
      }

      if (accessGroups.length) {
        if (question.idQuestion === 1905) {
          question.idAccessGroups = accessGroups.map(group => group.idGroup);
        }
      }

      return question;
    })
  } catch (e) {
    console.log("GENERAL - handleInitialAdjustments error", e)
  }
}

function groupMultipleFieldQuestions({
  mainIdQuestion,
  questionnaire,
  handleQuestionsInitialAdjustments
}) {
  try {
    const groupQuestions = [];

    const mainQuestions = questionnaire.filter(question => question.idQuestion === mainIdQuestion);
    for (const mainQuestion of mainQuestions) {
      let group = []

      const mainQuestionIndex = questionnaire.findIndex(question => question.idQuestion === mainIdQuestion);
      if (mainQuestionIndex || mainQuestionIndex == 0) group.push(questionnaire[mainQuestionIndex]);

      if (mainQuestionIndex != -1) questionnaire.splice(mainQuestionIndex, 1);

      if (mainQuestion.childrenQuestions) mainQuestion.childrenQuestions.map(childQuestion => group.push(childQuestion));

      group.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

      if (handleQuestionsInitialAdjustments) group = handleQuestionsInitialAdjustments({ questionnaire: group });

      groupQuestions.push(group.map((question, index) => ({ ...question, key: null })));
    }

    return groupQuestions;
  } catch (e) {
    console.log("GENERAL - groupMultipleFieldQuestions error", e)
  }
}

function sectionIsVisible({
  sectionName,
  questionnaire,
  permissions = {},
  currentModality,
  ignoreVisibilityByModality = false
}) {
  const permissionsValues = Object.values(permissions);

  return questionnaire
    .some(question => question?.behaviorObject?.sectionName === sectionName
      && question.visibleQuestion
      && (ignoreVisibilityByModality || !question.productPlans?.length || question.productPlans.includes(currentModality))
      && (
        !question?.behaviorObject?.idDomainAccessFilter ||
        !permissionsValues ||
        (!Array.isArray(question?.behaviorObject?.idDomainAccessFilter) ?
          permissionsValues.includes(question.behaviorObject.idDomainAccessFilter) :
          permissionsValues.find(v => v && question?.behaviorObject?.idDomainAccessFilter.includes(v))
        )
      )
    )
}

function createSectionRow({
  questions
}) {
  questions = JSON.parse(JSON.stringify(questions));
  return questions.map(question => {
    const { defaultValue } = question.behaviorObject || {};
    question.answer = defaultValue || "";
    question.answerJson = defaultValue || "";
    question.idAnswerOption = null;
    question.idPerson = null;
    question.idQuestionnaireAnswers = null;
    question.idQuestionnaireAnswersParent = null;
    question.idQuestionnaireAnswersRoot = null;
    question.key = null;

    if (question.behaviorObject?.extraExclamationWarning) {
      question.behaviorObject.extraExclamationWarning = false;
    }

    return question;
  })
}

function handleQuestionnaire({
  data,
  setQuestionnaire,
  personData,
  setFieldValue,
  users,
  accessGroups
}) {
  try {
    let questionnaire = [];
    questionnaire = [
      ...data.persons,
      ...data.operations,
      ...data.payments,
      ...data.risks,
    ];

    // 0 - GENERAL initial adjustments
    questionnaire = handleInitialAdjustments({ questionnaire, users, accessGroups, setFieldValue })

    // 1 - PERSON initial adjustments
    questionnaire = personHandler.events.handleInitialAdjustments({
      questionnaire,
      setQuestionnaire,
      setFieldValue,
      personData
    });

    // 2 - Sort questionnaire
    questionnaire = questionnaire.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

    setQuestionnaire(questionnaire);

    return questionnaire;
  } catch (e) {
    console.log("GENERAL - handleQuestionnaire error", e)
  }
}

function handleSave({
  values,
  questionnaire,
  idPerson,
  idOperation,
  idOperationParent,
  SECTIONS,
  isDraft,
  getDescription,
  sentUpdateAdminOperation,
  useUpdateAdminOperationRequest,
  mc,
  formikRef,
  history,
  userParentInfo,
  save,
  setFieldError,
  isUsernameAvailable,
  users,
  idUser,
  accessGroupIds
}) {

  buildValidation({
    questionnaire,
    idPerson,
    idOperation,
    idOperationParent,
    SECTIONS,
    useUpdateAdminOperationRequest,
    isDraft,
    getDescription,
    save,
    isUsernameAvailable
  }).validate(values, { abortEarly: false }).then(() => {
    const isValidSubmittedForm = handleValidationsSubmittedForm({ isDraft,
      questionnaire,
      setFieldError,
      mc,
      isUsernameAvailable,
      getDescription,
      users
     });

    if (isValidSubmittedForm) handleSubmit({
      values,
      questionnaire,
      idPerson,
      idOperation,
      idOperationParent,
      SECTIONS,
      sentUpdateAdminOperation,
      useUpdateAdminOperationRequest,
      history,
      userParentInfo,
      mc,
      getDescription,
      formikRef,
      idUser,
      accessGroupIds
    });

  }).catch((error) => {
    onValidationError({
      error,
      mc,
      formikRef,
      questionnaire,
      getDescription,
    })
  })
}

function _cpfValidation(cpf) {
  let sum;
  let rest;
  sum = 0;
  if (cpf == "00000000000") return false;

  for (let i = 1; i <= 9; i++) sum = sum + parseInt(cpf.substring(i - 1, i)) * (11 - i);
  rest = (sum * 10) % 11;

  if ((rest == 10) || (rest == 11)) rest = 0;
  if (rest != parseInt(cpf.substring(9, 10))) return false;

  sum = 0;
  for (let i = 1; i <= 10; i++) sum = sum + parseInt(cpf.substring(i - 1, i)) * (12 - i);
  rest = (sum * 10) % 11;

  if ((rest == 10) || (rest == 11)) rest = 0;
  if (rest != parseInt(cpf.substring(10, 11))) return false;
  return true;
}

function buildValidation({
  questionnaire,
  idPerson,
  idOperation,
  idOperationParent,
  SECTIONS,
  contacts,
  productPlansGroup,
  useUpdateAdminOperationRequest,
  isDraft,
  getDescription
}) {
  try {
    const validationSchema = {};

    const validationQuestionnaire = handleSaveQuestionnaireAnswer({
      questionnaire,
      idPerson,
      idOperation,
      idOperationParent,
      onlyQuestions: true,
      SECTIONS,
      contacts,
      productPlansGroup,
      useUpdateAdminOperationRequest,
    });

    let allQuestions = [...validationQuestionnaire];
    for (const question of allQuestions) {
      if (question.childrenQuestions?.length) allQuestions = [...allQuestions, ...question.childrenQuestions]
    }

    for (const question of allQuestions) {
      if (isDraft && ![].includes(question.idQuestion)) continue;
      if ([614].includes(question?.idDomainQuestion)) continue;
      if (!question?.key) continue;

      question?.childrenQuestions?.map(item => {
        if (item.visibleQuestion && item.fieldMask?.type == 'phone' && item.behaviorObject?.isRequired) {
          validationSchema[item.key] = yup.string().test({
            name: "answer-validation",
            message: getDescription("DOMAINS", 765),
            test: (value) => !!value && value.length >= 10
          })
        } else if (item.visibleQuestion && item.behaviorObject?.fieldMask == 'email') {
          if (item.answer) {
            validationSchema[item.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
          } else {
            if (item.behaviorObject?.isRequired) {
              validationSchema[item.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
            } else {
              validationSchema[item.key] = yup.string().notRequired();
            }
          }
        } else {
          validationSchema[item.key] = item.visibleQuestion && item.behaviorObject?.isRequired
            ? yup.string().test("answer-validation", getDescription("DOMAINS", 762), function (value) {
              return !!item.answer;
            }) : yup.string().notRequired();
        }
      })

      if (question.visibleQuestion && question.behaviorObject?.isRequired && question.fieldMask?.type == 'phone') {
        validationSchema[question.key] = yup.string().test({
          name: "answer-validation",
          message: getDescription("DOMAINS", 765),
          test: (value) => !!value && value.length >= 10
        })
      } else if (question.visibleQuestion && question.behaviorObject?.fieldMask == 'email') {
        if (question.answer) {
          validationSchema[question.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
        } else {
          if (question.behaviorObject?.isRequired) {
            validationSchema[question.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
          } else {
            validationSchema[question.key] = yup.string().notRequired();
          }
        }
      } else if (question.visibleQuestion && question.behaviorObject?.isRequired && question.fieldMask?.type == 'cpfCnpj' && question.answer?.length !== 14) {
        validationSchema[question.key] = yup.string().test({
          name: "cpf-validation",
          message: getDescription("DOMAINS", 1017),
          test: (value) => _cpfValidation(question.answer)
        })
      } else {
        validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
          ? yup.string().test("answer-validation", getDescription("DOMAINS", 762), function (value) {
            return !!question.answer;
          }) : yup.string().notRequired();
      }
    }

    return yup.object().shape(validationSchema);
  } catch (e) {
    console.log("GENERAL - buildValidation error", e)
  }
}

function handleValidationsSubmittedForm({
  isDraft,
  questionnaire,
  setFieldError,
  mc,
  isUsernameAvailable,
  getDescription,
  users
}) {
  if (isDraft) return true;

  let validationResult = true;

  let firstErrorKey = null;

  for (const question of questionnaire) {
    if (question.idQuestion === 2425 &&
      !isUsernameAvailable && !users) {
      setFieldError(question.key, getDescription('DOMAINS', 1018));
      mc.error(getDescription('DOMAINS', 1019));
      if (!firstErrorKey) firstErrorKey = question.key;
      
      validationResult = false;
    }
  }
  if (firstErrorKey) {
    const elements = global.window.document.getElementsByName(firstErrorKey);
    if (elements?.length) {
      setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
    }
  }

  return validationResult;
}

async function handleSubmit({
  questionnaire,
  SECTIONS,
  sentUpdateAdminOperation,
  mc,
  useUpdateAdminOperationRequest,
  idPerson,
  idPersonRoot,
  idOperation,
  idOperationParent,
  history,
  idUser,
  accessGroupIds
}) {
  if (sentUpdateAdminOperation.current) return;

  sentUpdateAdminOperation.current = true;

  handleSaveQuestionnaireAnswer({
    questionnaire,
    onlyQuestions: false,
    SECTIONS,
    mc,
    useUpdateAdminOperationRequest,
    idPerson,
    idPersonRoot,
    idOperation,
    idOperationParent,
    sentUpdateAdminOperation,
    history,
    idUser,
    accessGroupIds
  });
}

function handleSaveQuestionnaireAnswer({
  questionnaire,
  onlyQuestions,
  SECTIONS,
  mc,
  useUpdateAdminOperationRequest,
  idPerson,
  idPersonRoot,
  idOperation,
  idOperationParent,
  sentUpdateAdminOperation,
  history,
  idUser,
  accessGroupIds
}) {
  try {
    let questionnaireAnswers = cloneDeep(questionnaire);

    for (const [key, section] of Object.entries(SECTIONS)) {
      if (section.isMultiple) {
        let multipleFields = []
        switch (section.name) {
          default:
            multipleFields = [];
            break;
        }

        multipleFields?.map(group => {
          if (group?.length) {
            const rootQuestion = group.find(question => !question.idQuestionRoot);
            const childrenQuestions = group.filter(question => question.idQuestionRoot);
            rootQuestion.childrenQuestions = childrenQuestions;
            if (!onlyQuestions) {
              questionnaireAnswers = [...questionnaireAnswers, rootQuestion];
            } else {
              questionnaireAnswers = [...questionnaireAnswers, ...group];
            }
          }
        })
      }
    }

    let childrenQuestionIds = [];
    for (const question of questionnaireAnswers) {
      if (question) {
        if (
          (question.idDomainQuestion === 270 ||
            question.idDomainQuestion === 271 ||
            (question.idDomainQuestion === 44 && typeof question.answer === 'object')
          ) && question.answer
        ) {
          try {
            question.answer = JSON.stringify(question.answer)
          } catch (e) {
          }
        }

        if (SECTIONS[question.behaviorObject?.sectionName] && SECTIONS[question.behaviorObject?.sectionName].isMultiple) {
          if (question?.childrenQuestions?.length) {
            const idQuestions = question.childrenQuestions.map(question => question.idQuestion);
            childrenQuestionIds = [...childrenQuestionIds, ...idQuestions];
          }
        } else {
          question.childrenQuestions = [];
        }
      }
    }

    questionnaireAnswers = questionnaireAnswers.filter(v => !childrenQuestionIds.includes(v.idQuestion));

    questionnaireAnswers = questionnaireAnswers.filter(v => 
      [
        "id_person_site_owner",
        "id_person_site_owner_role"
      ].includes(v.dsQuestion) ||
      [
        "PERSON",
        "PERSON_USER", 
        "PRODUCT_PLAN"
      ].includes(v.behaviorObject.sectionName));

    if (onlyQuestions) return questionnaireAnswers;

    useUpdateAdminOperationRequest({
      idPerson,
      idPersonRoot,
      idOperation,
      idOperationParent,
      questionnaire: questionnaireAnswers,
      idUser,
      accessGroupIds
    }).then((resp) => {
      if (resp.status !== 200) return;

      const { data: { value, errors } } = resp;

      if (value?.idPerson) history.push(`/management/58/person/edit/${value?.idPerson}`);

      if (errors?.length > 0) {
        errors.forEach((error) => {
          console.log(error.message);
          mc.error(`Erro ao executar a operação.`);
        });
      }
    }).finally(() => {
      sentUpdateAdminOperation.current = false;
    });
  } catch (e) {
    mc.error(e)
    console.log("GENERAL - handleSaveQuestionnaireAnswer error", e)
  }
}

function onValidationError({
  error,
  mc,
  formikRef,
  questionnaire,
  getDescription
}) {
  try {
    questionnaire = cloneDeep(questionnaire);

    mc.error(getDescription("DOMAINS", 760))
    const allQuestions = questionnaire;

    let firstErrorKey = null;
    for (const question of allQuestions) {
      if (!formikRef.current.errors || !formikRef.current.errors[question.key]) continue;
      formikRef.current.setFieldError(question.key, null);
    }

    error.inner?.map(({ message, path }) => {
      const question = allQuestions.find(question => question.key === path);
      if (question.dsQuestion === "CNPJ / CPF") mc.error(message);
      else if (question.dsQuestion !== "E-mail") mc.error(getDescription("DOMAINS", 764).replace('@@dsQuestion', question.dsQuestion));
      else mc.error(getDescription("DOMAINS", 761));
      if (message.includes(["CPF inválido"])) formikRef.current.setFieldError(question?.key, `* ${message}`);
      else if (!message.includes(["E-mail", "Telefone"])) formikRef.current.setFieldError(question?.key, `* ${getDescription("DOMAINS", 762)}`);
      else formikRef.current.setFieldError(question?.key, `* ${getDescription("DOMAINS", 763)}`);
      if (!firstErrorKey) firstErrorKey = question.key;
    })

    if (firstErrorKey) {
      const elements = global.window.document.getElementsByName(firstErrorKey);
      if (elements?.length) {
        setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
      }
    }
  } catch (error) {
    console.log('onValidationError catch', error)
  }
}


const events = {
  handleInitialAdjustments,
  groupMultipleFieldQuestions,
  sectionIsVisible,
  createSectionRow,
  handleQuestionnaire,
  handleSaveQuestionnaireAnswer,
  handleSave,
}

export { events };